import { SeasonTray, SeasonTrayContentSort } from '../../../domain/season-tray';
import {
  CreateTvSeasonPageItemRequestApiModel,
  GenericLayoutApiModel,
  SortOrderApiModel,
  UpdateTvSeasonPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import { platformTargetingMapper, targetingMapperDomainToApi } from '../../mapping/create-tray-domain-to-api';
import { TvSeasonPageItemApiModel } from '@cxb/editor-stb-api/model/models';
import {
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  mapApiModelImageWithPlatformToDomain,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { SeasonTrayFormViewModel, SeasonTraySortViewModel } from './season-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapDomainImagesToPlatformLocalizedViewModelImage,
  mapSchedulingDomainToViewModel,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { mapViewModelImagesToDomainImages } from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'SeasonTray'

/**
 * DOMAIN -> API
 */

export const mapSeasonTrayDomainModelToApiModel = (
  seasonTray: SeasonTray,
  pageVersionId?: string,
  tenantId?: string
): CreateTvSeasonPageItemRequestApiModel & UpdateTvSeasonPageItemRequestApiModel => {
  const commonProperties = mapperPipe(seasonTray, targetingMapperDomainToApi, platformTargetingMapper);

  return remainderMapper(commonProperties, () => ({
    descriptionLocalized: seasonTray.description,
    descriptionVisible: true,
    layout: GenericLayoutApiModel.Collection,
    nameLocalized: seasonTray.title,
    pageVersionId,
    seriesId: seasonTray.program?.id?.toString(),
    sortOrder: seasonTray.sort === SeasonTrayContentSort.Ascending ? SortOrderApiModel.Ascending : SortOrderApiModel.Descending,
    backgroundImages: seasonTray.backgroundImages.map(({ id, platformId }) => ({ imageId: id, platformId })),
    tenantId,
  }));
};

/**
 * API->DOMAIN
 */

export const mapSeasonTrayApiModelToDomainModel = (pageItem: TvSeasonPageItemApiModel): SeasonTray => {
  const splitSeriesId = pageItem.seriesId.split('/');
  const seriesId = splitSeriesId[splitSeriesId.length - 1];

  return remainderMapper(
    mapperPipe(
      pageItem,
      commonMapper,
      scheduledDateMapperApiToDomain,
      descriptionLocalizedMapperApiToDomain,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain
    ),
    () => ({
      type: 'SeasonTray',
      title: pageItem.nameLocalized,
      description: pageItem.descriptionLocalized,
      urlSlug: pageItem.slug,
      seriesId,
      backgroundImages: pageItem.backgroundImages.map(mapApiModelImageWithPlatformToDomain),
      sort: pageItem.sortOrder === SortOrderApiModel.Ascending ? SeasonTrayContentSort.Ascending : SeasonTrayContentSort.Descending,
      program: pageItem['program'],
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapSeasonTrayDomainModelToViewModel = (tray: SeasonTray): SeasonTrayFormViewModel => {
  return {
    id: tray.id,
    backgroundImages: mapDomainImagesToPlatformLocalizedViewModelImage(tray.backgroundImages),
    countryIds: tray.countryIds,
    platformIds: tray.platformIds,
    description: tray.description,
    seriesId: tray.seriesId,
    scheduling: mapSchedulingDomainToViewModel(tray),
    title: tray.title,
    type: TrayFormViewModelType.SeasonTray,
    userSegmentIds: tray.userSegmentIds,
    sort: tray.sort === SeasonTrayContentSort.Ascending ? SeasonTraySortViewModel.Ascending : SeasonTraySortViewModel.Descending,
    program: tray.program,
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapSeasonTrayViewModelToDomainModel = (vm: SeasonTrayFormViewModel, platformIds: Record<PlatformType, string>): SeasonTray => {
  return {
    backgroundImages: mapViewModelImagesToDomainImages(vm.backgroundImages, platformIds),
    countryIds: vm.countryIds,
    platformIds: vm.platformIds,
    description: vm.description,
    draggable: false,
    seriesId: vm.seriesId,
    id: vm.id,
    order: undefined,
    scheduledFrom: vm.scheduling?.scheduledFrom ? new Date(vm.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: vm.scheduling?.scheduledTo ? new Date(vm.scheduling.scheduledTo).getTime() : undefined,
    title: vm.title,
    type: 'SeasonTray',
    userSegmentIds: vm.userSegmentIds,
    visible: undefined,
    sort: vm.sort === SeasonTraySortViewModel.Ascending ? SeasonTrayContentSort.Ascending : SeasonTrayContentSort.Descending,
    program: vm.program,
  };
};
